import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAdmin } from '../OPSDashboard';

const AdminLogin = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  
  const { login, adminUser } = useAdmin();

  // Redirect if already authenticated
  useEffect(() => {
    if (adminUser) {
      // User is already authenticated, no need to redirect
      // The parent component will handle showing the dashboard
    }
  }, [adminUser]);

  // Security: Track login attempts (increased limit since backend is now stable)
  useEffect(() => {
    const storedAttempts = localStorage.getItem('admin_login_attempts');
    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts));
    }
  }, []);

  // Clear lockout function
  const clearLockout = () => {
    localStorage.removeItem('admin_login_attempts');
    setAttempts(0);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Security: Limit login attempts (increased to 10 for production stability)
    if (attempts >= 10) {
      setError('Too many failed attempts. Click "Reset Login Attempts" below to try again.');
      return;
    }

    setLoading(true);
    setError('');

    const result = await login(email, password);

    if (result.success) {
      localStorage.removeItem('admin_login_attempts');
      // Success will be handled by the parent component via adminUser state change
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem('admin_login_attempts', newAttempts.toString());
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen admin-panel flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back button */}
        {onBack && (
          <div className="flex justify-start">
            <button
              onClick={onBack}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        )}

        {/* Security Warning */}
        <div className="admin-card p-4 border-red-200 bg-red-50">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <div className="text-sm text-red-800">
              <p className="font-medium">Restricted Access</p>
              <p>This is an internal administration panel. Unauthorized access is prohibited.</p>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="admin-card">
          <div className="px-8 py-10">
            <div className="text-center mb-8">
              <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Lexa.run Admin OPS
              </h2>
              <p className="mt-2 text-gray-600">
                Secure administration dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                  {attempts >= 5 && (
                    <p className="text-xs text-red-600 mt-1">
                      Warning: {10 - attempts} attempts remaining
                    </p>
                  )}
                  {attempts >= 10 && (
                    <button
                      type="button"
                      onClick={clearLockout}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Reset Login Attempts
                    </button>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter admin email"
                  disabled={loading || attempts >= 5}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter admin password"
                    disabled={loading || attempts >= 5}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading || attempts >= 5}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || attempts >= 5}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  loading || attempts >= 5
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:ring-blue-500'
                }`}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Authenticating...
                  </>
                ) : attempts >= 5 ? (
                  'Access Locked'
                ) : (
                  'Secure Login'
                )}
              </button>
            </form>

            {/* Security Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-xs text-gray-500 space-y-1">
                <p>🔒 All admin sessions are encrypted and logged</p>
                <p>🚨 Unauthorized access attempts are monitored</p>
                <p>⏱️ Sessions expire after 8 hours for security</p>
              </div>
            </div>
          </div>
        </div>

        {/* Development Notice */}
        <div className="text-center">
          <p className="text-sm text-gray-400">
            Lexa.run Admin OPS Dashboard v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;